/**
 * Input Method Selector Component
 *
 * Allows users to choose between three input methods for creature creation:
 * - Draw It: Canvas drawing interface
 * - Describe It: Text description input
 * - Upload It: Image file upload
 */

import React from 'react';
import { usePromptBuilderStore, type InputMethod } from '@/stores/usePromptBuilderStore';
import styles from './InputMethodSelector.module.css';

/**
 * SVG Icons for input methods
 */
function DrawIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 19l7-7 3 3-7 7-3-3z" />
      <path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z" />
      <path d="M2 2l7.586 7.586" />
      <circle cx="11" cy="11" r="2" />
    </svg>
  );
}

function DescribeIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
    </svg>
  );
}

function UploadIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="17 8 12 3 7 8" />
      <line x1="12" y1="3" x2="12" y2="15" />
    </svg>
  );
}

interface InputMethodOption {
  id: InputMethod;
  icon: React.ReactNode;
  label: string;
  ariaLabel: string;
}

const inputMethods: InputMethodOption[] = [
  {
    id: 'draw',
    icon: <DrawIcon />,
    label: 'Draw It',
    ariaLabel: 'Draw It - Use canvas to draw your creature'
  },
  {
    id: 'text',
    icon: <DescribeIcon />,
    label: 'Describe It',
    ariaLabel: 'Describe It - Write a text description'
  },
  {
    id: 'upload',
    icon: <UploadIcon />,
    label: 'Upload It',
    ariaLabel: 'Upload It - Upload an image file'
  }
];

export const InputMethodSelector: React.FC = () => {
  const inputMethod = usePromptBuilderStore((state) => state.inputMethod);
  const setInputMethod = usePromptBuilderStore((state) => state.setInputMethod);

  const handleMethodSelect = (method: InputMethod) => {
    setInputMethod(method);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>, method: InputMethod) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleMethodSelect(method);
    }
  };

  return (
    <div className={styles.container} role="group" aria-label="Input method selector">
      {inputMethods.map((method) => (
        <button
          key={method.id}
          type="button"
          className={`${styles.button} ${inputMethod === method.id ? styles.active : ''}`}
          onClick={() => handleMethodSelect(method.id)}
          onKeyDown={(e) => handleKeyDown(e, method.id)}
          aria-label={method.ariaLabel}
          aria-pressed={inputMethod === method.id}
          tabIndex={0}
        >
          <span className={styles.icon} aria-hidden="true">
            {method.icon}
          </span>
          <span className={styles.label}>{method.label}</span>
        </button>
      ))}
    </div>
  );
};
